/**
 * Boot timing and cache-hit diagnostics module.
 *
 * Provides a single namespace for instrumenting the critical boot path:
 *   - mark()       — record a named timing event
 *   - cacheCheck() — check QueryClient cache and record result
 *   - summary()    — emit structured log + Sentry breadcrumb/tag
 *
 * mark() and cacheCheck() always record their data so summary() can emit a
 * meaningful Sentry breadcrumb and `boot.prefetch.allHit` tag for production
 * observability, and so lib/perf/perfTrace.ts can assemble the full boot
 * timeline. The verbose `[boot] cache-check` and `[boot] summary` log lines
 * are gated behind BOOT_DEBUG to keep production console output quiet.
 *
 * BOOT_DEBUG is on in dev builds and whenever the unified perf flag is set
 * (`?ahoy_perf=1`, `localStorage['ahoy:perf']`, or the legacy `boot-debug`
 * key) — see lib/perf/perfFlag.ts.
 */

import * as Sentry from '@sentry/react';
import type { QueryClient } from '@tanstack/react-query';

import { createLogger } from '@/lib/logger';
import { isPerfTraceEnabled } from '@/lib/perf/perfFlag';

// Created on first verbose use, not at import: this module is imported from
// the auth/org boot path, and an import-time logger construction would make
// every module on that path depend on the logger being fully initialised.
let bootLogger: ReturnType<typeof createLogger> | null = null;
function getBootLogger(): ReturnType<typeof createLogger> {
  bootLogger ??= createLogger({ module: 'boot' });
  return bootLogger;
}

// ---------------------------------------------------------------------------
// BOOT_DEBUG flag
// ---------------------------------------------------------------------------

function computeBootDebug(): boolean {
  return import.meta.env.DEV || isPerfTraceEnabled();
}

/** Test-only override. null means "use live computation". */
let bootDebugTestOverride: boolean | null = null;

/** Snapshot of BOOT_DEBUG at module load. Exported for backward compat
 * with tests that read the flag directly. */
export const BOOT_DEBUG: boolean = computeBootDebug();

/**
 * Test-only — override the BOOT_DEBUG flag for the current test.
 * Pass null to restore live computation.
 */
export function setBootDebugForTest(value: boolean | null): void {
  bootDebugTestOverride = value;
}

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

export interface BootMark {
  name: string;
  t: number;
  [key: string]: unknown;
}

/** Maximum number of marks retained in the array (drop-oldest when exceeded). */
export const MAX_MARKS = 500;

let marks: BootMark[] = [];
let marksTruncationWarned = false;
let summarized = false;
const markedOnce = new Set<string>();
const markListeners = new Set<(mark: BootMark) => void>();

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Record a named timing event with optional extra data.
 * Always records — recorded marks are read by summary() to compute totalMs and
 * to emit the Sentry boot breadcrumb in production. The verbose [boot] log
 * line in summary() is the only thing gated behind BOOT_DEBUG.
 */
export function mark(name: string, data?: Record<string, unknown>): void {
  const entry: BootMark = { name, t: performance.now(), ...data };
  if (marks.length >= MAX_MARKS) {
    if (!marksTruncationWarned) {
      marksTruncationWarned = true;
      console.warn(
        `[boot] marks array reached MAX_MARKS (${MAX_MARKS}); dropping oldest mark.`
      );
    }
    marks.shift();
  }
  marks.push(entry);
  for (const listener of markListeners) {
    listener(entry);
  }
}

/**
 * Record `name` the first time it is called and ignore repeats. For "first
 * time X happened" stage marks emitted from render-cycle effects that may
 * re-run (skeleton shown, section first painted, data first arrived).
 */
export function markOnce(name: string, data?: Record<string, unknown>): void {
  if (markedOnce.has(name)) {
    return;
  }
  markedOnce.add(name);
  mark(name, data);
}

/** Every mark recorded so far, in order. Read-only snapshot. */
export function getMarks(): readonly BootMark[] {
  return marks;
}

/**
 * Subscribe to marks as they are recorded. Returns the unsubscribe function.
 * Used by lib/perf/perfTrace.ts to finalize the trace on `dashboard-ready`.
 */
export function onMark(listener: (mark: BootMark) => void): () => void {
  markListeners.add(listener);
  return () => {
    markListeners.delete(listener);
  };
}

/**
 * Check whether the QueryClient cache has data for `key` and record result.
 * Returns { hit, hasData } for caller use.
 *
 * Always queries the cache and records the result so the production Sentry
 * `boot.prefetch.allHit` tag in summary() reflects the real cache state.
 * Only the per-key bootLogger.info verbose log is gated behind BOOT_DEBUG.
 */
export function cacheCheck(
  label: string,
  queryClient: QueryClient,
  key: readonly unknown[]
): { hit: boolean; hasData: boolean } {
  const data = queryClient.getQueryData(key);
  const hit = data !== undefined;
  const hasData = hit && data !== null;

  marks.push({
    name: 'cache-check',
    t: performance.now(),
    label,
    hit,
    key: JSON.stringify(key),
  });

  // Verbose per-key log — read isPerfTraceEnabled() live so ?ahoy_perf=1 takes
  // effect on the current load without a reload.
  if (bootDebugTestOverride ?? (import.meta.env.DEV || isPerfTraceEnabled())) {
    getBootLogger().info(`[boot] cache-check: ${label}`, {
      label,
      hit,
      hasData,
      key: JSON.stringify(key),
    });
  }

  return { hit, hasData };
}

/**
 * Emit a summary log line with all recorded marks and aggregated cache hits.
 * Also emits a Sentry breadcrumb and tag for production observability.
 * Idempotent — only runs once per page load.
 * Always emits a Sentry breadcrumb and `boot.prefetch.allHit` tag (production
 * observability). The verbose `[boot] summary` log line is gated behind
 * BOOT_DEBUG (dev-only).
 */
export function summary(): void {
  if (summarized) {
    return;
  }
  summarized = true;

  const cacheMarks = marks.filter(m => m.name === 'cache-check');
  const cacheHits: Record<string, boolean> = {};
  for (const m of cacheMarks) {
    if (typeof m.label === 'string') {
      cacheHits[m.label] = m.hit as boolean;
    }
  }

  const firstMark = marks[0];
  const lastMark = marks[marks.length - 1];
  const totalCold = firstMark && lastMark ? lastMark.t - firstMark.t : 0;

  const proposalsHit = cacheHits['proposals'] ?? false;
  const meetingsHit = cacheHits['meetings'] ?? false;
  const currentUserEntityHit = cacheHits['currentUserEntity'] ?? false;
  const onboardingHit = cacheHits['onboarding'] ?? false;
  const importHit = cacheHits['import'] ?? false;
  const allHit =
    proposalsHit &&
    meetingsHit &&
    currentUserEntityHit &&
    onboardingHit &&
    importHit;
  const totalMs = Math.round(totalCold);

  // Verbose log — read isPerfTraceEnabled() live so ?ahoy_perf=1 takes
  // effect on the current load without a reload.
  if (bootDebugTestOverride ?? (import.meta.env.DEV || isPerfTraceEnabled())) {
    getBootLogger().info('[boot] summary', {
      marks,
      totalCold,
      cacheHits,
    });
  }

  // Production observability — fires regardless of BOOT_DEBUG.
  Sentry.addBreadcrumb({
    category: 'boot.prefetch',
    level: 'info',
    message: 'boot summary',
    data: {
      proposalsHit,
      meetingsHit,
      currentUserEntityHit,
      onboardingHit,
      importHit,
      totalMs,
    },
  });

  Sentry.setTag('boot.prefetch.allHit', allHit ? 'true' : 'false');
}

/**
 * Reset module state for testing.
 * Exported ONLY for test use — do not call in application code.
 */
export function resetForTest(): void {
  marks = [];
  marksTruncationWarned = false;
  summarized = false;
  markedOnce.clear();
  markListeners.clear();
  bootDebugTestOverride = null;
}
